/**
 * Configuration Setting Types
 *
 * Single source of truth for ALL types related to the Configuration Setting module.
 * Includes API response types, frontend types, form types, and constants.
 *
 * Field names match the API response directly — no mapping layer.
 */

// ─── API Response Types ─────────────────────────────────────────────────────────

/** API target setting item (as returned by the backend) */
export interface ApiTargetSetting {
  category_subItem: string;
  target_value: number;
  dri: string;
}

/** API program data (as returned by the backend) */
export interface ApiProgramData {
  program_id: string;
  program_name: string;
  cm: string;
  cm_location: string;
  operation: string;
  eom: string;
  ok2_ship: string;
  product_family_id: string;
  product_family_name: string;
  pvt_date: string;
  status: "enabled" | "disabled";
  version: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  target_settings: ApiTargetSetting[];
}

/** API product family item */
export interface ApiProductFamily {
  product_family_id: string;
  product_family_name: string;
  description?: string;
  is_active?: boolean;
}

/** CM item with associated locations from API */
export interface ApiCmItem {
  cm: string;
  locations: string[];
}

/** Program options from API: programs, sub_programs, cms (with nested locations), operations */
export interface ApiProgramOptions {
  programs: string[];
  sub_programs: string[];
  cms: ApiCmItem[];
  operations: string[];
}

/** Category duplicate check result */
export interface ApiCategoryCheckResult {
  exists: boolean;
  message: string;
}

/** Batch update result */
export interface ApiBatchUpdateResult {
  total: number;
  success: number;
  failure: number;
  failed_programs: Array<{
    program_id: string;
    program_name: string;
    error: string;
  }>;
}

// ─── Redux State Interface ──────────────────────────────────────────────────────

export interface ConfigurationState {
  // Programs list
  programs: ApiProgramData[];
  programsLoading: boolean;
  programsError: string | null;

  // Single program detail
  programDetail: ApiProgramData | null;
  programDetailLoading: boolean;
  programDetailError: string | null;

  // Create program
  createProgramLoading: boolean;
  createProgramResult: { program_id: string; message: string } | null;
  createProgramError: string | null;

  // Update programs (single or batch)
  updateProgramsLoading: boolean;
  updateProgramsResult: ApiBatchUpdateResult | null;
  updateProgramsError: string | null;

  // Update programs status (enable/disable)
  updateStatusLoading: boolean;
  updateStatusResult: { message: string; updated_count: number } | null;
  updateStatusError: string | null;

  // Product families
  productFamilies: ApiProductFamily[];
  productFamiliesLoading: boolean;
  productFamiliesError: string | null;

  // Create product family
  createProductFamilyLoading: boolean;
  createProductFamilyResult: ApiProductFamily | null;
  createProductFamilyError: string | null;

  // Category duplicate check
  categoryCheckLoading: boolean;
  categoryCheckResult: ApiCategoryCheckResult | null;
  categoryCheckError: string | null;

  // Program options (cascading: program → cm → location)
  programOptions: ApiProgramOptions | null;
  programOptionsLoading: boolean;
  programOptionsError: string | null;
}

// ─── Frontend Types ─────────────────────────────────────────────────────────────

// Stage types for production lines
export type StageType = 'SMT' | 'FATP';

// Built-in target category types
export type BuiltInTargetCategory = 'Testing' | 'Machine Downtime' | 'Assembly' | 'Output' | 'Material';

// Target category can be built-in or custom (string)
export type TargetCategory = BuiltInTargetCategory | string;

// Target setting item — matches API target_settings structure directly
export interface TargetSettingItem {
  category_subItem: string;
  target_value: number | string;
  dri: string;
  isCustom?: boolean; // Flag for user-created custom categories (frontend only)
  isNewlyAdded?: boolean; // Flag for categories added in the current session (frontend only)
}

// Legacy stage detail item (kept for backward compatibility)
export interface StageDetail {
  stage: StageType;
  detail: string;
  dri: string;
}

// Legacy target item (kept for backward compatibility)
export interface TargetItem {
  name: string;
  value: string;
  dri: string;
}

// Program status
export type ProgramStatus = 'enabled' | 'disabled';

// Program data structure — matches API response directly, no mapping
export interface ProgramData {
  program_id: string;
  program_name: string;
  cm: string;
  cm_location: string;
  operation: string;
  eom: string;
  ok2_ship: string;
  product_family_id: string;
  product_family_name: string;
  pvt_date: string;
  program_mapping: string[];  // Sub-program names that belong to this program (e.g., baton_upper, baton_lower for baton)
  ur_only: boolean;  // Whether this program is UR (Unrestricted) only, defaults to false
  status: ProgramStatus;
  version: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  target_settings: TargetSettingItem[];
}

/**
 * Helper: convert a string array to DropdownOption[]
 */
const toDropdownOptions = (items: string[]): DropdownOption[] =>
  items.map((item) => ({ label: item, value: item }));

/**
 * Extract dropdown options from API program options.
 * cms is now an array of { cm, locations } objects.
 * Returns DropdownOption[] arrays ready to use in Select components.
 */
export function extractProgramOptions(programOptions: ApiProgramOptions | null) {
  if (!programOptions) {
    return {
      programNames: [] as DropdownOption[],
      subPrograms: [] as DropdownOption[],
      allCms: [] as DropdownOption[],
      allLocations: [] as DropdownOption[],
      allOperations: [] as DropdownOption[],
    };
  }

  // Extract CM names from the cms array of objects
  const cmNames = (programOptions.cms || []).map((item) => item.cm);
  // Extract all unique locations from all CMs
  const allLocationSet = new Set<string>();
  (programOptions.cms || []).forEach((item) => {
    (item.locations || []).forEach((loc) => allLocationSet.add(loc));
  });

  return {
    programNames: toDropdownOptions(programOptions.programs || []),
    subPrograms: toDropdownOptions(programOptions.sub_programs || []),
    allCms: toDropdownOptions(cmNames),
    allLocations: toDropdownOptions(Array.from(allLocationSet)),
    allOperations: toDropdownOptions(programOptions.operations || []),
  };
}

/**
 * Get locations filtered by selected CM from API program options.
 * Returns DropdownOption[] for the locations of a specific CM.
 */
export function getLocationsForCm(programOptions: ApiProgramOptions | null, selectedCm: string): DropdownOption[] {
  if (!programOptions || !selectedCm) {
    // If no CM selected, return all locations
    if (!programOptions) return [];
    const allLocationSet = new Set<string>();
    (programOptions.cms || []).forEach((item) => {
      (item.locations || []).forEach((loc) => allLocationSet.add(loc));
    });
    return toDropdownOptions(Array.from(allLocationSet));
  }

  const cmItem = (programOptions.cms || []).find((item) => item.cm === selectedCm);
  if (!cmItem) return [];
  return toDropdownOptions(cmItem.locations || []);
}

/**
 * Convert API product families to dropdown options
 */
export function mapProductFamiliesToOptions(families: ApiProductFamily[]): DropdownOption[] {
  return families.map((f) => ({
    label: f.product_family_name,
    value: f.product_family_id,
  }));
}

// ─── Column Visibility Configuration ──────────────────────────────────────────

// Column visibility configuration (session-based)
export interface ColumnVisibility {
  program_name: boolean;
  cm: boolean;
  cm_location: boolean;
  operation: boolean;
  eom: boolean;
  ok2_ship: boolean;
  product_family_name: boolean;
  program_mapping: boolean;
  pvt_date: boolean;
  ur_only: boolean;
  status: boolean;
  created_by: boolean;
  created_at: boolean;
  updated_by: boolean;
  updated_at: boolean;
  // Target-related columns (hidden by default)
  testingSmtTarget: boolean;
  testingFatpTarget: boolean;
  machineDowntimeSmtTarget: boolean;
  machineDowntimeFatpTarget: boolean;
  assemblyFatpTarget: boolean;
  outputSmtTarget: boolean;
  outputFatpTarget: boolean;
  materialSmtTarget: boolean;
  materialFatpTarget: boolean;
}

// All available column IDs
export type ColumnId = keyof ColumnVisibility;

// Column metadata for settings
export interface ColumnMeta {
  id: ColumnId;
  label: string;
  alwaysVisible?: boolean;
  group?: string; // Group label for organizing in settings
}

// All available columns metadata
export const ALL_COLUMNS: ColumnMeta[] = [
  { id: 'program_name', label: 'Program Name', alwaysVisible: true },
  { id: 'program_mapping', label: 'Sub-Programs' },
  { id: 'cm', label: 'CM' },
  { id: 'cm_location', label: 'Location' },
  { id: 'operation', label: 'Operation' },
  { id: 'eom', label: 'EOM' },
  { id: 'ok2_ship', label: 'OK2SHIP' },
  { id: 'product_family_name', label: 'Product Family' },
  { id: 'pvt_date', label: 'PVT Date' },
  { id: 'ur_only', label: 'Factory Factor only' },
  { id: 'status', label: 'Status' },
  { id: 'created_by', label: 'Creator' },
  { id: 'created_at', label: 'Created DateTime' },
  { id: 'updated_by', label: 'Updater' },
  { id: 'updated_at', label: 'Updated DateTime' },
  // Target columns — order matches DEFAULT_TARGET_SETTINGS
  { id: 'assemblyFatpTarget', label: 'Assembly', group: 'Target Settings' },
  { id: 'testingSmtTarget', label: 'SMT', group: 'Target Settings' },
  { id: 'testingFatpTarget', label: 'Testing', group: 'Target Settings' },
  { id: 'machineDowntimeSmtTarget', label: 'Machine Downtime-SMT', group: 'Target Settings' },
  { id: 'machineDowntimeFatpTarget', label: 'Machine Downtime-FATP', group: 'Target Settings' },
  { id: 'outputSmtTarget', label: 'Output-SMT', group: 'Target Settings' },
  { id: 'outputFatpTarget', label: 'Output-FATP', group: 'Target Settings' },
  { id: 'materialSmtTarget', label: 'Material-SMT', group: 'Target Settings' },
  { id: 'materialFatpTarget', label: 'Material-FATP', group: 'Target Settings' },
];

// Default column visibility
export const DEFAULT_COLUMN_VISIBILITY: ColumnVisibility = {
  program_name: true,
  cm: true,
  cm_location: true,
  operation: true,
  eom: true,
  ok2_ship: true,
  product_family_name: true,
  program_mapping: true,
  pvt_date: true,
  ur_only: false,
  status: true,
  created_by: true,
  created_at: true,
  updated_by: true,
  updated_at: true,
  // Target columns hidden by default — order matches DEFAULT_TARGET_SETTINGS
  assemblyFatpTarget: false,
  testingSmtTarget: false,
  testingFatpTarget: false,
  machineDowntimeSmtTarget: false,
  machineDowntimeFatpTarget: false,
  outputSmtTarget: false,
  outputFatpTarget: false,
  materialSmtTarget: false,
  materialFatpTarget: false,
};

// ─── Form Data Types ──────────────────────────────────────────────────────────

// Form data for Step 1 (Basic Info) — uses API field names directly
export interface BasicInfoFormData {
  program_name: string;
  cm: string;
  cm_location: string;
  operation: string;
  eom: string;
  ok2_ship: string;
  product_family_name: string; // display name
  product_family_id: string;   // for API
  pvt_date: string;
  program_mapping: string[];  // Sub-program names mapped to this program (e.g., baton_upper, baton_lower)
  ur_only: boolean | undefined;  // Whether this program is Factory Factor only, defaults to false. undefined means "no change" in bulk edit mode.
}

// Form data for Step 2 (Target Settings) — just an array of TargetSettingItem
export type TargetSettingsFormData = TargetSettingItem[];

// Legacy form data for Step 2 (kept for backward compatibility)
export interface StageTargetFormData {
  stageDetails: StageDetail[];
  targets: TargetItem[];
}

// Creation method
export type CreationMethod = 'clone' | 'scratch';

// Edit mode types
export type EditMode = 'create' | 'editSingle' | 'editBulk';

// Wizard step
export type WizardStep = 1 | 2;

// Submit result for bulk edit
export interface BulkEditResult {
  total: number;
  success: number;
  failure: number;
  failedPrograms: string[];
}

// Options for dropdowns
export interface DropdownOption {
  label: string;
  value: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────────

// Default target settings template — uses API field names
export const DEFAULT_TARGET_SETTINGS: TargetSettingItem[] = [
  { category_subItem: 'Assembly', target_value: '', dri: '' },// FATP only
  { category_subItem: 'SMT', target_value: '', dri: '' },// SMT only
  { category_subItem: 'Testing', target_value: '', dri: '' },// FATP only
  { category_subItem: 'Machine Downtime-SMT', target_value: '', dri: '' },
  { category_subItem: 'Machine Downtime-FATP', target_value: '', dri: '' },
  { category_subItem: 'Output-SMT', target_value: '', dri: '' },
  { category_subItem: 'Output-FATP', target_value: '', dri: '' },
  { category_subItem: 'Material-SMT', target_value: '', dri: '' },
  { category_subItem: 'Material-FATP', target_value: '', dri: '' },
];

// Target category configuration - which stages apply to each category
export const TARGET_CATEGORY_CONFIG: Record<string, { hasSmt: boolean; hasFatp: boolean; description: string }> = {
  'Assembly': { hasSmt: false, hasFatp: true, description: 'FATP Assembly Only' },
  'Testing': { hasSmt: false, hasFatp: true, description: 'FATP Testing' },
  'SMT': { hasSmt: true, hasFatp: false, description: 'SMT Testing' },
  'Machine Downtime-SMT': { hasSmt: true, hasFatp: false, description: 'SMT Machine Downtime' },
  'Machine Downtime-FATP': { hasSmt: false, hasFatp: true, description: 'FATP Machine Downtime' },
  'Output-SMT': { hasSmt: true, hasFatp: false, description: 'SMT Output' },
  'Output-FATP': { hasSmt: false, hasFatp: true, description: 'FATP Output' },
  'Material-SMT': { hasSmt: true, hasFatp: false, description: 'Material-SMT Management' },
  'Material-FATP': { hasSmt: false, hasFatp: true, description: 'Material-FATP Management' },
};

// Default config for custom categories (both SMT and FATP enabled)
export const DEFAULT_CUSTOM_CATEGORY_CONFIG = { hasSmt: true, hasFatp: true, description: 'Custom Category' };

// Operation options (hardcoded list)
export const OPERATION_OPTIONS: DropdownOption[] = [
  { label: 'North Ops', value: 'North Ops' },
  { label: 'South Ops', value: 'South Ops' },
  { label: 'Ring', value: 'Ring' },
];

// Legacy static options — kept for backward compatibility but now loaded from API
export const CM_OPTIONS: DropdownOption[] = [
  { label: 'Foxconn', value: 'Foxconn' },
  { label: 'Pegatron', value: 'Pegatron' },
  { label: 'Luxshare', value: 'Luxshare' },
  { label: 'Wistron', value: 'Wistron' },
  { label: 'Compal', value: 'Compal' },
  { label: 'Quanta', value: 'Quanta' },
];

export const SITE_OPTIONS: DropdownOption[] = [
  { label: 'Shenzhen', value: 'Shenzhen' },
  { label: 'Shanghai', value: 'Shanghai' },
  { label: 'Zhengzhou', value: 'Zhengzhou' },
  { label: 'Chengdu', value: 'Chengdu' },
  { label: 'Kunshan', value: 'Kunshan' },
  { label: 'Chennai', value: 'Chennai' },
];

export const PRODUCT_FAMILY_OPTIONS: DropdownOption[] = [
  { label: 'ECHO', value: 'ECHO' },
  { label: 'FIRE_TV_ACCESSORIES', value: 'FIRE_TV_ACCESSORIES' },
  { label: 'FTV_TV', value: 'FTV_TV' },
  { label: 'APO', value: 'APO' },
  { label: 'EREADER', value: 'EREADER' },
];

export const PROGRAM_NAME_OPTIONS: DropdownOption[] = [
  { label: 'D63 Proto', value: 'D63 Proto' },
  { label: 'D63 EVT', value: 'D63 EVT' },
  { label: 'D63 DVT', value: 'D63 DVT' },
  { label: 'D63 PVT', value: 'D63 PVT' },
  { label: 'N85 MP', value: 'N85 MP' },
  { label: 'N85 Ramp', value: 'N85 Ramp' },
  { label: 'J510 Ramp', value: 'J510 Ramp' },
  { label: 'J510 MP', value: 'J510 MP' },
  { label: 'M3 Ultra', value: 'M3 Ultra' },
  { label: 'M3 Max', value: 'M3 Max' },
  { label: 'W9 Series', value: 'W9 Series' },
  { label: 'W10 Series', value: 'W10 Series' },
  { label: 'V1 Pro', value: 'V1 Pro' },
  { label: 'A16 Proto', value: 'A16 Proto' },
];

// SMT Stage Detail options (legacy)
export const SMT_DETAIL_OPTIONS: DropdownOption[] = [
  { label: 'Solder Paste Printing', value: 'Solder Paste Printing' },
  { label: 'Component Placement', value: 'Component Placement' },
  { label: 'Reflow Soldering', value: 'Reflow Soldering' },
  { label: 'AOI Inspection', value: 'AOI Inspection' },
  { label: 'X-Ray Inspection', value: 'X-Ray Inspection' },
  { label: 'ICT Testing', value: 'ICT Testing' },
];

// FATP Stage Detail options (legacy)
export const FATP_DETAIL_OPTIONS: DropdownOption[] = [
  { label: 'Assembly', value: 'Assembly' },
  { label: 'Functional Test', value: 'Functional Test' },
  { label: 'Calibration', value: 'Calibration' },
  { label: 'Software Loading', value: 'Software Loading' },
  { label: 'Cosmetic Inspection', value: 'Cosmetic Inspection' },
  { label: 'Packaging', value: 'Packaging' },
];

// DRI options (sample team members)
export const DRI_OPTIONS: DropdownOption[] = [
  { label: 'Alice Wang', value: 'Alice Wang' },
  { label: 'Bob Chen', value: 'Bob Chen' },
  { label: 'Charlie Li', value: 'Charlie Li' },
  { label: 'Diana Zhang', value: 'Diana Zhang' },
  { label: 'Edward Liu', value: 'Edward Liu' },
  { label: 'Fiona Wu', value: 'Fiona Wu' },
  { label: 'George Huang', value: 'George Huang' },
  { label: 'Helen Xu', value: 'Helen Xu' },
];
